import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang, catLabel, ALERT_STATUS } from "../lib/i18n";
import { LangToggle } from "../components/LangToggle";
import { Radio, ArrowLeft, Send, MessageCircle, Phone, Mail, RotateCw, Loader2, TriangleAlert } from "lucide-react";

const CHANNEL_META = {
  telegram: { icon: Send, key: "ch_telegram" },
  whatsapp: { icon: MessageCircle, key: "ch_whatsapp" },
  call: { icon: Phone, key: "ch_call" },
  call_retry: { icon: Phone, key: "ch_call_retry" },
  client_quote: { icon: Mail, key: "ch_client_quote" },
  client_eta: { icon: Mail, key: "ch_client_eta" },
};

const CHANNELS = ["telegram", "whatsapp", "call", "client_quote", "client_eta", "call_retry"];
const STATUSES = ["sent", "failed", "pending", "config_required", "skipped"];

export default function AlertsHistory() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [resending, setResending] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (channel) params.channel = channel;
    if (status) params.status = status;
    const { data } = await api.get("/admin/alerts-history", { params });
    setRows(data);
  }, [channel, status]);

  useEffect(() => { load(); }, [load]);

  const resend = async (orderId) => {
    setResending(orderId);
    try {
      await api.post(`/orders/${orderId}/resend-alerts`);
      toast.success(t("resend_sent"));
      setTimeout(load, 1200);
    } catch (e) {
      toast.error(t("none"));
    } finally {
      setResending("");
    }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString(lang === "it" ? "it-IT" : "en-GB") : "—");

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-700 bg-slate-900 px-4 py-3 text-white sm:px-8">
        <div className="flex items-center gap-2">
          <button data-testid="alerts-back-btn" onClick={() => nav("/centrale")} className="rounded-full bg-slate-800 p-2"><ArrowLeft className="h-4 w-4" /></button>
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600"><Radio className="h-4 w-4" /></div>
          <p className="font-display text-sm font-extrabold leading-none">ProntoSof24 · {t("alerts_history")}</p>
        </div>
        <LangToggle />
      </header>

      <div className="mx-auto max-w-5xl space-y-4 p-4 sm:p-8">
        <div className="grid grid-cols-2 gap-2 sm:max-w-md">
          <select data-testid="alerts-filter-channel" value={channel} onChange={(e) => setChannel(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
            <option value="">{t("al_channel")}: {t("al_all")}</option>
            {CHANNELS.map((c) => <option key={c} value={c}>{t(CHANNEL_META[c].key)}</option>)}
          </select>
          <select data-testid="alerts-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
            <option value="">{t("al_status")}: {t("al_all")}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{t(ALERT_STATUS[s]?.key || s)}</option>)}
          </select>
        </div>

        {rows === null && <p className="text-sm text-slate-400">{t("loading")}</p>}
        {rows?.length === 0 && <p data-testid="alerts-empty" className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-400">{t("al_no_history")}</p>}

        <div className="space-y-2">
          {rows?.map((r, i) => {
            const meta = CHANNEL_META[r.channel] || { icon: Send, key: r.channel };
            const Icon = meta.icon;
            const st = ALERT_STATUS[r.status] || ALERT_STATUS.pending;
            return (
              <div key={`${r.order_id}-${r.channel}-${i}`} data-testid={`alert-row-${r.channel}-${r.status}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600"><Icon className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs font-bold text-blue-600">{r.code}</span>
                    {r.is_demo && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">DEMO</span>}
                    {(r.urgency === "emergenza" || r.urgency === "urgente") && <span className="flex items-center gap-0.5 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-extrabold text-rose-700"><TriangleAlert className="h-3 w-3" />{t("urgent")}</span>}
                    <span className="text-xs font-semibold text-slate-700">{t(meta.key)}</span>
                    <span className="text-xs text-slate-400">· {r.customer_name} · {catLabel(r.category, lang)}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{t("al_when")}: {fmt(r.at)}{r.reason ? ` · ${r.reason}` : ""}{r.error ? ` · ${r.error}` : ""}</p>
                </div>
                <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-bold ${st.cls}`}>{t(st.key)}</span>
                <button data-testid={`alert-resend-${r.order_id}`} onClick={() => resend(r.order_id)} disabled={resending === r.order_id} className="flex shrink-0 items-center gap-1 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">
                  {resending === r.order_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}{t("resend")}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
